// Cloudflare Turnstile nos pontos do Auth que exigem CAPTCHA. Sem site key no
// config.js fica inerte: não carrega script, não mostra nada e não manda token.
// O token é de uso único: reset() depois de cada chamada ao Auth, com ou sem erro.
import { t } from "./strings/en";

interface Turnstile {
  render(el: HTMLElement, options: Record<string, unknown>): string;
  reset(id: string): void;
}

const SCRIPT = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
let loading: Promise<Turnstile> | null = null;

/** Site key do Turnstile, ou null quando o CAPTCHA está desligado neste ambiente. */
export function turnstileSiteKey(raw: { turnstileSiteKey?: unknown } | undefined): string | null {
  const key = raw?.turnstileSiteKey;
  return typeof key === "string" && key.trim() !== "" ? key.trim() : null;
}

function loadTurnstile(): Promise<Turnstile> {
  loading ??= new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SCRIPT;
    script.async = true;
    script.onload = () => {
      const api = (window as { turnstile?: Turnstile }).turnstile;
      if (api) resolve(api);
      else reject(new Error("turnstile ausente"));
    };
    script.onerror = () => reject(new Error("turnstile não carregou"));
    document.head.append(script);
  });
  return loading;
}

export interface Captcha {
  /** Mensagem a mostrar se ainda não dá para enviar o formulário; null quando pode. */
  blocker(): string | null;
  /** Token para `options.captchaToken`; undefined com o CAPTCHA desligado. */
  token(): string | undefined;
  reset(): void;
}

const INERT: Captcha = { blocker: () => null, token: () => undefined, reset: () => {} };

export function mountCaptcha(container: HTMLElement): Captcha {
  const sitekey = turnstileSiteKey((globalThis as { AULRA_CONFIG?: { turnstileSiteKey?: unknown } }).AULRA_CONFIG);
  if (!sitekey) return INERT;
  container.hidden = false;
  container.setAttribute("aria-label", t("captchaLabel"));
  let token: string | undefined;
  let failed = false;
  let widget: string | null = null;
  let api: Turnstile | null = null;
  const theme = document.documentElement.dataset.theme;
  loadTurnstile()
    .then((loaded) => {
      api = loaded;
      widget = loaded.render(container, {
        sitekey,
        theme: theme === "light" || theme === "dark" ? theme : "auto",
        size: "flexible",
        callback: (value: string) => {
          token = value;
          failed = false;
        },
        "expired-callback": () => (token = undefined),
        "error-callback": () => {
          token = undefined;
          failed = true;
        },
      });
    })
    .catch(() => (failed = true));
  return {
    blocker: () => (token ? null : t(failed ? "captchaFailed" : "captchaPending")),
    token: () => token,
    reset: () => {
      token = undefined;
      failed = false;
      if (api && widget !== null) api.reset(widget);
    },
  };
}

/** Erro do Auth por CAPTCHA recusado ou vencido (o token já foi gasto). */
export function isCaptchaError(error: { code?: string } | null | undefined): boolean {
  return error?.code === "captcha_failed";
}
